import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useMatchProfileService } from "~pages/MatchProfiles/hooks/useMatchProfileService.ts";
import { useFetchMatchProfileList } from "~pages/MatchProfiles/MatchProfileListPage/hooks/useFetchMatchProfileList.ts";
import { useGetMatchProfileTableColumns } from "~pages/MatchProfiles/MatchProfileListPage/hooks/useGetMatchProfileTableColumns.tsx";
import type { FixtureProfile } from "~types/fixtureProfile.ts";

const DEFAULT_SORTER = { columnKey: "name", order: "ascend" as const };

const MatchProfileListPage = () => {
  const paginationConfig = useHandlePagination(10, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [massEditMode, setMassEditMode] = useState(false);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.matchProfiles.listPage;
  const columns = useGetMatchProfileTableColumns(massEditMode);

  const { resetPagination } = paginationConfig;

  const handleSearch = useCallback(
    (value: string) => {
      resetPagination();
      setSearchText(value);
    },
    [resetPagination],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      nameContains: searchText,
    }),
    [paginationConfig.pageSize, paginationConfig.offset, searchText, sortBy],
  );

  const { data, refetch, isPending } = useFetchMatchProfileList({
    customParams: listParams,
    enabled: sortBy !== null,
  });
  const { deleteMatchProfile, isDeletePending } = useMatchProfileService();

  const getNavigateUrl = (profileId = "") => `/${customerId}/admin/${sport}/${organizationId}/match-profiles/manage/${profileId}`;

  return (
    <DataTable<FixtureProfile>
      handleRefresh={async () => {
        await refetch();
      }}
      title={formatMessage({ id: "match.profiles" })}
      columns={columns}
      rowActions={{
        base: {
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.profileId)),
          },
          delete: {
            onClick: (row) => deleteMatchProfile(row.profileId),
            loading: isDeletePending,
          },
        },
      }}
      rowKey={"profileId"}
      rowNameKey={"name"}
      dataSource={data?.data || []}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.matchProfiles`}
      isDataLoading={isPending}
      handleAdd={() => navigate(getNavigateUrl())}
      addButtonIntlKey={"match.profile"}
      searchConfig={{
        handleSearch,
        searchText,
      }}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.matchProfiles`,
        defaultSorter: DEFAULT_SORTER,
        onSortChange: setSortBy,
      }}
      permissionNames={[PermissionNames.MATCH_PROFILE]}
      onMassEditModeChangedHandler={setMassEditMode}
      massEditMode={massEditMode}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default MatchProfileListPage;
